import { useMemo } from 'react';
import { BookOpen, Users } from 'lucide-react';
import { COMPETENCES_ORDRE, SUPPORTS_ORDRE, progressionComprehension, taux } from './progressionParCompetence';
import { nomDuTexte } from '../EvalNat/evaluationConfig';

/**
 * La progression en compréhension de l'écrit sur une forme parallèle (11/10/2026) : le QCM de suivi porte sur
 * d'autres textes que septembre, donc la classe et chaque élève se comparent par support et par compétence,
 * jamais question par question. Les nombres de questions sont affichés : une compétence comptée sur une ou deux
 * questions ne se lit pas comme un taux sur dix. Une forme sans seuils (4e, écrite pour TanoEd) ne donne pas de
 * groupe : seul celui de septembre s'affiche.
 */
const LIBELLE_SUPPORT = { litteraire: 'Texte littéraire', documentaire: 'Support documentaire' };
const LIBELLE_COMPETENCE = {
    explicite: 'Retrouver une information explicite',
    inference: 'Mettre en relation, faire des inférences',
    sens_global: 'Comprendre le sens global et la visée',
};
const COULEUR_GROUPE = {
    'À besoins': 'bg-red-100 text-red-700',
    'A besoins': 'bg-red-100 text-red-700',
    Fragile: 'bg-amber-100 text-amber-800',
    Satisfaisant: 'bg-emerald-100 text-emerald-700',
    'Très Satisfaisant': 'bg-emerald-200 text-emerald-800',
};

const Taux = ({ compte }) => {
    const t = taux(compte);
    if (t === null) return <span className="text-gray-300">non passé</span>;
    return (
        <span className="font-semibold text-gray-800">
            {t} %<span className="ml-1 text-[11px] font-normal text-gray-400">({compte.justes}/{compte.total})</span>
        </span>
    );
};

const Evolution = ({ avant, apres }) => {
    const a = taux(avant);
    const b = taux(apres);
    if (a === null || b === null) return <span className="text-gray-300">·</span>;
    const ecart = b - a;
    const couleur = ecart >= 5 ? 'text-emerald-600' : ecart <= -5 ? 'text-red-600' : 'text-gray-500';
    return <span className={`font-bold ${couleur}`}>{ecart > 0 ? '+' : ''}{ecart} pts</span>;
};

const Groupe = ({ nom }) => (nom
    ? <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${COULEUR_GROUPE[nom] || 'bg-gray-100 text-gray-600'}`}>{nom}</span>
    : <span className="text-gray-300 text-xs">non connu</span>);

const LigneClasse = ({ libelle, avant, apres, accent = false }) => (
    <tr className={accent ? 'bg-blue-50/60' : ''}>
        <td className={`px-4 py-2.5 text-sm ${accent ? 'font-bold text-[#0f2a4a]' : 'text-gray-700'}`}>{libelle}</td>
        <td className="px-4 py-2.5 text-sm"><Taux compte={avant} /></td>
        <td className="px-4 py-2.5 text-sm"><Taux compte={apres} /></td>
        <td className="px-4 py-2.5 text-sm"><Evolution avant={avant} apres={apres} /></td>
    </tr>
);

const ProgressionComprehension = ({ evalNat, evalBilan, forme }) => {
    const { classe, eleves } = useMemo(() => progressionComprehension({ evalNat, evalBilan, forme }), [evalNat, evalBilan, forme]);
    const textesSeptembre = forme.remplace.map((t) => `« ${nomDuTexte(t)} »`).join(' et ');
    const textesForme = forme.textes.map((t) => `« ${t.SUPPORT.titre} »`).join(' et ');
    const avecGroupe = Boolean(forme.seuils);
    // Par élève : ceux qui ont passé le QCM de suivi ; les autres tiennent en une phrase (12/10/2026 : 26 lignes
    // « non passé » noyaient le seul élève qui l'avait rendu)
    const elevesDuSuivi = eleves.filter((e) => e.bilan);
    const nbSansSuivi = eleves.length - elevesDuSuivi.length;

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-800">Compréhension de l&apos;écrit : septembre, puis le QCM de suivi</h3>
                </div>
                <p className="px-5 pt-4 text-sm text-gray-600 leading-relaxed">
                    En septembre, les élèves ont lu {textesSeptembre} ; le QCM de suivi porte sur d&apos;autres textes, {textesForme}.
                    {' '}{forme.origine} On compare donc par support et par compétence, pas question par question.
                    {' '}{classe.eleves > 1
                        ? `La classe compte les ${classe.eleves} élèves qui ont passé les deux.`
                        : classe.eleves === 1
                            ? 'Un seul élève a passé les deux : les taux ci-dessous sont les siens, pas ceux de la classe.'
                            : 'Aucun élève n’a encore passé les deux.'}
                </p>
                <div className="p-5 overflow-x-auto">
                    <table className="w-full min-w-[560px] border border-gray-100 rounded-xl overflow-hidden">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">La classe</th>
                                <th className="px-4 py-2.5 font-semibold">Septembre</th>
                                <th className="px-4 py-2.5 font-semibold">QCM de suivi</th>
                                <th className="px-4 py-2.5 font-semibold">Évolution</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            <LigneClasse libelle="Ensemble" avant={classe.septembre.global} apres={classe.bilan.global} accent />
                            {SUPPORTS_ORDRE.map((s) => (
                                <LigneClasse key={s} libelle={LIBELLE_SUPPORT[s]} avant={classe.septembre.parSupport[s]} apres={classe.bilan.parSupport[s]} />
                            ))}
                            {COMPETENCES_ORDRE.map((c) => (
                                <LigneClasse key={c} libelle={LIBELLE_COMPETENCE[c]} avant={classe.septembre.parCompetence[c]} apres={classe.bilan.parCompetence[c]} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-800">Par élève</h3>
                </div>
                <p className="px-5 pt-4 text-sm text-gray-600 leading-relaxed">
                    {avecGroupe ? (
                        <>
                            Le groupe du QCM de suivi suit les seuils de l&apos;outil du ministère sur ses {forme.textes.reduce((n, t) => n + t.questions.length, 0)} questions
                            {' '}({forme.seuils.aBesoinsMax} ou moins : à besoins ; jusqu&apos;à {forme.seuils.fragileMax} : fragile ; au-delà : satisfaisant), seulement si l&apos;élève a passé les deux textes.
                        </>
                    ) : (
                        <>
                            Ces textes n&apos;ont pas de seuils étalonnés : le QCM de suivi ne donne pas de groupe, seulement des taux.
                            {' '}Le groupe affiché est celui de septembre.
                        </>
                    )}
                    {nbSansSuivi > 0
                        ? ` ${nbSansSuivi} élève${nbSansSuivi > 1 ? 's' : ''} de septembre n’${nbSansSuivi > 1 ? 'ont' : 'a'} pas encore passé le QCM de suivi.`
                        : ''}
                </p>
                <div className="p-5 overflow-x-auto">
                    <table className="w-full min-w-[760px] border border-gray-100 rounded-xl overflow-hidden">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                <th className="px-4 py-2.5 font-semibold">Élève</th>
                                <th className="px-4 py-2.5 font-semibold">{avecGroupe ? 'Groupe' : 'Groupe de septembre'}</th>
                                <th className="px-4 py-2.5 font-semibold">Ensemble</th>
                                {SUPPORTS_ORDRE.map((s) => <th key={s} className="px-4 py-2.5 font-semibold">{LIBELLE_SUPPORT[s]}</th>)}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {elevesDuSuivi.map((e) => (
                                <tr key={`${e.nom}|${e.classe}`}>
                                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{e.nom}</td>
                                    <td className="px-4 py-2.5 text-sm">
                                        {avecGroupe ? (
                                            <span className="inline-flex items-center gap-1.5"><Groupe nom={e.groupeSeptembre} /> <span className="text-gray-300">→</span> <Groupe nom={e.groupeBilan} /></span>
                                        ) : (
                                            <Groupe nom={e.groupeSeptembre} />
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-sm">
                                        <Taux compte={e.septembre?.global} /> <span className="text-gray-300">→</span> <Taux compte={e.bilan?.global} />
                                    </td>
                                    {SUPPORTS_ORDRE.map((s) => (
                                        <td key={s} className="px-4 py-2.5 text-sm">
                                            <Taux compte={e.septembre?.parSupport[s]} /> <span className="text-gray-300">→</span> <Taux compte={e.bilan?.parSupport[s]} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProgressionComprehension;
